import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './merchant-policy.css';
const credentialError = e => (e.response?.data?.code ?? e.code) === 5033
    ? 'PIN 잠금·초기화 API가 비활성 상태입니다. 로그인·송금 사용 여부는 PIN 사용 설정에서 별도로 확인해주세요.'
    : e.response?.data?.msg || e.message || 'PIN 상태를 조회하지 못했습니다.';

export default function MerchantPinAdmin({ mid, role }) {
    const [data, setData] = useState(null);
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [recovery, setRecovery] = useState(null);
    const [reveal, setReveal] = useState(false);
    const [now, setNow] = useState(Date.now());
    const locked = useRef(false);
    const generation = useRef(0);
    const path = `${process.env.REACT_APP_API}/api/security/admin/merchant/${encodeURIComponent(mid || '')}`;
    const read = async () => {
        const response = await axios.get(path, { withCredentials: true, timeout: 15000 });
        if (response.data?.code !== 1000) throw Object.assign(new Error(response.data?.msg || 'PIN 상태 조회에 실패했습니다.'), {code:response.data?.code});
        const result = response.data.data;
        if (!Number.isInteger(result?.version) || typeof result?.csrf_token !== 'string' || !result.csrf_token) throw new Error('PIN 상태 버전 또는 관리자 CSRF를 확인하지 못했습니다.');
        return result;
    };
    const load = async () => {
        if (!mid || role !== 'SUPER' || locked.current) return;
        const current = generation.current;
        locked.current = true; setBusy(true); setData(null); setError('');
        try { const result = await read(); if (current === generation.current) setData(result); }
        catch (e) { if (current === generation.current) setError(credentialError(e)); }
        finally { if (current === generation.current) { locked.current = false; setBusy(false); } }
    };
    const loader = useRef(load); loader.current = load;
    useEffect(() => {
        setData(null); setReason(''); setRecovery(null); setReveal(false); setError(''); locked.current = false;
        void loader.current();
        const invalidate = () => { generation.current += 1; locked.current = false; };
        return invalidate;
    }, [mid, role]);
    useEffect(() => {
        if (!recovery) return;
        const tick = () => { const time = Date.now(); setNow(time); if (recovery.expiresAt <= time) { setRecovery(null); setReveal(false); } };
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [recovery]);
    const mutate = async action => {
        if (role !== 'SUPER' || !mid || !data || locked.current || recovery || !reason.trim()) return;
        if (action === 'unlock' && data.recovery_required) return;
        if (!window.confirm(`${mid} 가맹점의 PIN ${action === 'reset' ? '초기화' : '잠금 해제'}를 진행하시겠습니까?\n사유: ${reason.trim()}\n기존 인증 세션과 권한이 무효화됩니다.${action === 'reset' ? '\n초기화 후 복구 권한으로 새 PIN을 등록해야 합니다.' : ''}`)) return;
        const current = generation.current;
        locked.current = true; setBusy(true); setError('');
        try {
            const response = await axios.post(path, { action, expected_version: data.version, reason: reason.trim() },
                { withCredentials: true, timeout: 15000, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': data.csrf_token } });
            if (current !== generation.current) return;
            if (response.data?.code !== 1000) throw new Error(response.data?.msg || 'PIN 관리 요청을 처리하지 못했습니다.');
            if (action === 'reset') {
                const result = response.data.data;
                if (typeof result?.recovery_authorization !== 'string' || !result.recovery_authorization || !Number.isFinite(Number(result.expires_in)) || Number(result.expires_in) <= 0) throw new Error('초기화 응답의 복구 권한을 확인하지 못했습니다. 자동 재요청하지 말고 운영 상태를 확인해주세요.');
                setRecovery({ value: result.recovery_authorization, expiresAt: Date.now() + Number(result.expires_in) * 1000 }); setNow(Date.now()); setReveal(false);
            }
            setReason(''); setData(null);
            const updated = await read();
            if (current === generation.current) setData(updated);
        } catch (e) {
            if (current === generation.current) { setData(null); setError(e.response?.data?.msg || e.message || '처리 결과를 확인하지 못했습니다. 자동 재요청하지 말고 상태를 조회해주세요.'); }
        } finally { if (current === generation.current) { locked.current = false; setBusy(false); } }
    };
    if (role !== 'SUPER') return <p>SUPER 권한이 필요합니다.</p>;
    return <section className="merchant-policy merchant-pin-admin" aria-busy={busy}>
        <header><span className="merchant-policy-tag">{mid || '-'}</span><h2>PIN 잠금·초기화</h2><p>로그인·송금 ON/OFF는 PIN 사용 설정에서 관리합니다.</p></header>
        <div className="merchant-policy-note">본인 확인 후 처리해주세요. PIN 초기화 시 재등록이 필요합니다.</div>
        {error && <p className="merchant-policy-error" role="alert">{error}</p>}
        {busy && <p role="status">처리 중입니다.</p>}
        {data && <dl><dt>PIN 등록</dt><dd>{data.pin_registered ? '등록됨' : '미등록'}</dd>
            <dt>복구 필요</dt><dd>{data.recovery_required ? '필요' : '없음'}</dd>
            <dt>잠금 해제 예정</dt><dd>{Number(data.locked_until) > 0 ? new Date(Number(data.locked_until)).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) : '-'}</dd>
            <dt>상태 버전</dt><dd>{data.version}</dd></dl>}
        {recovery && <div role="status" style={{ margin: '16px 0', padding: 12, border: '1px solid #d97706' }}>
            <p>본인 확인 후 승인된 경로로 전달해주세요. 화면을 닫으면 복구 권한을 다시 표시할 수 없습니다.</p>
            <label>일회용 복구 권한<input readOnly autoComplete="off" type={reveal ? 'text' : 'password'} value={recovery.value} style={{ width: '100%' }} /></label>
            <p>남은 시간: {Math.max(0, Math.ceil((recovery.expiresAt - now) / 1000))}초</p>
            <button type="button" onClick={() => setReveal(value => !value)}>{reveal ? '권한 숨기기' : '권한 표시'}</button>
            <button type="button" onClick={() => { setRecovery(null); setReveal(false); }}>전달 완료 · 화면에서 지우기</button>
        </div>}
        <label className="merchant-policy-reason">처리 사유<textarea value={reason} onChange={e => setReason(e.target.value)} disabled={busy || !data || Boolean(recovery)} maxLength={500} rows={2} style={{ display: 'block', width: '100%' }} /></label>
        <div className="merchant-policy-actions">
            <button type="button" disabled={busy || !data || Boolean(recovery) || !reason.trim() || data.recovery_required} onClick={() => mutate('unlock')}>잠금 해제</button>
            <button type="button" disabled={busy || !data || Boolean(recovery) || !reason.trim()} onClick={() => mutate('reset')}>PIN 초기화</button>
            <button type="button" disabled={busy} onClick={load}>상태 다시 조회</button>
        </div>
        {data?.recovery_required && <p>복구 절차 진행 중에는 잠금 해제로 취소할 수 없습니다.</p>}
        <div className="merchant-policy-history"><h3>잠금·초기화 이력</h3>
        {!data ? <p>PIN 상태 조회 후 이력을 확인할 수 있습니다.</p> : !Array.isArray(data.history) ? <p>이력 정보를 확인하지 못했습니다.</p> : data.history.length === 0 ? <p>표시할 이력이 없습니다.</p> : <ul>{data.history.map((entry, index) => <li key={index}>{entry.created_at || '-'} · {entry.action || '-'} · {entry.reason || '-'}</li>)}</ul>}</div>
    </section>;
}
